import React from 'react';
import { ArrowLeft } from 'lucide-react';
import PrimaryButton from './PrimaryButton';
import PrimaryButtonLight from './PrimaryButtonLight';

export interface ActionButtonConfig {
  key: string;
  label: string;
  onClick: () => void;
  variant: 'primary' | 'secondary' | 'danger' | 'success' | 'warning';
  disabled?: boolean;
  loading?: boolean;
  loadingLabel?: string;
  icon?: React.ReactNode;
  hidden?: boolean;
  className?: string;
}

export interface FormPageLayoutProps {
  onBack: () => void;
  actions: ActionButtonConfig[];
  children: React.ReactNode;
  title?: string;
  subtitle?: string;
  headerRight?: React.ReactNode;
  className?: string;
  hideBack?: boolean;
  hideHeaderOnDesktop?: boolean;
}

interface ButtonSpinnerProps {
  textColorClass?: string;
}

const ButtonSpinner: React.FC<ButtonSpinnerProps> = ({ textColorClass = 'text-white' }) => (
  <svg className={`animate-spin mr-2 h-4 w-4 ${textColorClass}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
  </svg>
);

function spinnerColorForVariant(variant: ActionButtonConfig['variant']): string {
  switch (variant) {
    case 'primary': return 'text-white';
    case 'secondary': return 'text-slate-800';
    case 'danger': return 'text-red-600';
    case 'success': return 'text-white';
    case 'warning': return 'text-amber-600';
    default: return 'text-white';
  }
}

interface ActionButtonProps {
  config: ActionButtonConfig;
}

const ActionButton: React.FC<ActionButtonProps> = ({ config }) => {
  const isDisabled = config.disabled || config.loading;

  const content = config.loading ? (
    <div className="flex items-center">
      <ButtonSpinner textColorClass={spinnerColorForVariant(config.variant)} />
      <span>{config.loadingLabel || config.label}</span>
    </div>
  ) : (
    <div className="flex items-center">
      {config.icon && <span className="mr-1">{config.icon}</span>}
      <span>{config.label}</span>
    </div>
  );

  switch (config.variant) {
    case 'primary':
      return (
        <PrimaryButton
          type="button"
          onClick={config.onClick}
          disabled={isDisabled}
          className={config.className}
        >
          {content}
        </PrimaryButton>
      );
    case 'secondary':
      return (
        <PrimaryButtonLight
          type="button"
          onClick={config.onClick}
          disabled={isDisabled}
          className={config.className}
        >
          {content}
        </PrimaryButtonLight>
      );
    case 'danger':
      return (
        <button
          type="button"
          onClick={config.onClick}
          disabled={isDisabled}
          className={`px-3 py-1.5 text-sm font-medium text-red-600 border border-red-300 rounded-md hover:bg-red-50 disabled:opacity-50 transition-colors ${config.className ?? ''}`}
        >
          {content}
        </button>
      );
    case 'success':
      return (
        <button
          type="button"
          onClick={config.onClick}
          disabled={isDisabled}
          className={`px-3 py-1.5 text-sm font-medium text-white bg-emerald-600 rounded-md hover:bg-emerald-700 disabled:opacity-50 transition-colors ${config.className ?? ''}`}
        >
          {content}
        </button>
      );
    case 'warning':
      return (
        <button
          type="button"
          onClick={config.onClick}
          disabled={isDisabled}
          className={`px-3 py-1.5 text-sm font-medium text-amber-600 border border-amber-300 rounded-md hover:bg-amber-50 disabled:opacity-50 transition-colors ${config.className ?? ''}`}
        >
          {content}
        </button>
      );
  }
};

const FormPageLayout: React.FC<FormPageLayoutProps> = ({
  onBack, actions, children, title, subtitle, headerRight, className, hideBack, hideHeaderOnDesktop
}) => {
  const visibleActions = actions.filter(a => !a.hidden);

  return (
    <div className={`bg-white rounded-xl ${className ?? ''}`}>

      {/* TOP BAR — back button (left) + action buttons (right) */}
      <div className={`${hideHeaderOnDesktop ? 'md:hidden ' : ''}flex items-center justify-between px-2 py-1.5 border-b border-slate-100`}>
        {/* Left: back button + title + headerRight */}
        <div className="flex items-center gap-3">
          {!hideBack && (
            <button onClick={onBack} type="button" className="p-1.5 text-slate-400 hover:text-slate-600 rounded-md hover:bg-slate-50">
              <ArrowLeft size={16} />
            </button>
          )}
          {(title || subtitle) && (
            <div>
              {title && <h2 className="text-base font-semibold text-slate-900">{title}</h2>}
              {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
            </div>
          )}
          {headerRight && (
            <div className="flex items-center gap-2">{headerRight}</div>
          )}
        </div>
        {/* Right: action buttons */}
        <div className="flex items-center gap-2 shrink-0">
          {visibleActions.map(action => <ActionButton key={action.key} config={action} />)}
        </div>
      </div>

      {/* CONTENT AREA */}
      <div className="p-2 space-y-2">
        {children}
      </div>

      {/* BOTTOM BAR — mirrors top-bar action buttons (by design: same actions at top and bottom for long forms) */}
      {visibleActions.length > 0 && (
        <div className="flex justify-end gap-2 px-2 py-1.5">
          {visibleActions.map(action => <ActionButton key={action.key} config={action} />)}
        </div>
      )}
    </div>
  );
};

export default FormPageLayout;
